// EmployeeIdCard.jsx
// Employee ID Card sub-module of the Certificate module.
//   • Select an employee (branch / designation filters + searchable list)
//   • Auto-fill details; photo auto-loads from the employee record, else manual upload
//   • Live preview of a CR80 (54mm × 86mm) front + back card — mirrors the student
//     ID-card "green wave" olive design, with employee-appropriate field labels
//   • Print (one card per side, like the student card)
//
// The card branding (school name, logo, address, phone, principal signature) is
// pulled from the institute's saved ID-card template so it matches the student card.
// If no ID-card template exists, the institution name from cookies is used.

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Select from 'react-select';
import * as Cookies from 'es-cookie';
import { ID_CARD_PRINCIPAL_SIG_KEY } from './BulkIdCards';

// ─── Helpers ────────────────────────────────────────────────────────────────

// Normalise a profile-photo value into a usable <img src>. (Same rules as the
// student flow: data URIs / URLs as-is, bare base64 gets a data: prefix.)
const normalizePhotoSrc = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  if (s.startsWith('data:') || /^https?:\/\//i.test(s) || s.startsWith('//')) return s;
  if (/^\/9j\//.test(s))                       return `data:image/jpeg;base64,${s}`;
  if (/^iVBORw0KGgo/.test(s))                  return `data:image/png;base64,${s}`;
  if (/^R0lGOD/.test(s))                       return `data:image/gif;base64,${s}`;
  if (/^[A-Za-z0-9+/]{200,}={0,2}$/.test(s))   return `data:image/jpeg;base64,${s}`;
  return s; // relative/other URL — let the browser try; onerror falls back
};

// Modern red ID-card theme (matches the supplied reference design).
const RED      = '#E1232A'; // primary vivid red
const RED_DARK = '#9E1B1F'; // dark maroon — top-right fold / footer accent
const ACCENT   = RED;       // drives the surrounding builder UI too

// Card colour themes. Aya staff get the blue card; every other designation keeps red.
// (Only the CARD is themed — the surrounding builder UI stays on ACCENT.)
const THEME_RED  = { main: RED,       dark: RED_DARK };
const THEME_BLUE = { main: '#29A3DC', dark: '#1668A5' };
// Support staff — Aya (incl. Aaya/Ayah), Driver, Watchman — carry the blue card.
const BLUE_ROLES = /\b(a+ya+h?|driver|watch\s?man)\b/i;
const themeFor = (designation) =>
  (BLUE_ROLES.test(String(designation || '')) ? THEME_BLUE : THEME_RED);

// ─── ID card markup (employee red-modern: front + back) ──────────────────────

// Sized in px (not mm): 54mm × 86mm @ 96dpi = 204 × 325px. html2canvas 1.4.1
// mis-scales mm units (narrow card + clipped header on export); px renders
// identically on screen and in the capture. The print path re-forces mm via CSS.
const ID_W = '204px';
const ID_H = '325px';
const ID_FONT = "Arial, Helvetica, sans-serif";

// White inline-SVG glyphs (render reliably in html2canvas + the print iframe,
// unlike webfont icons which need external CSS).
const SVG_MAIL   = `<svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm1.4 2L12 12l7.6-5H4.4zM20 8.4l-8 5.2-8-5.2V18h16V8.4z"/></svg>`;
const SVG_PHONE  = `<svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.6c.6 0 1 .4 1 1 0 1.2.2 2.5.6 3.6.1.3 0 .7-.2 1l-2.4 2.2z"/></svg>`;
// Role / designation — briefcase glyph.
const SVG_ROLE   = `<svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M9 4h6a2 2 0 0 1 2 2v1h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3V6a2 2 0 0 1 2-2zm0 3h6V6H9v1zM4 9v9h16V9H4z"/></svg>`;
// Subject — open book glyph.
const SVG_BOOK   = `<svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M12 5.5C10.4 4.6 8.3 4 6 4c-1.4 0-2.8.2-4 .6v14c1.2-.4 2.6-.6 4-.6 2.3 0 4.4.6 6 1.5 1.6-.9 3.7-1.5 6-1.5 1.4 0 2.8.2 4 .6v-14C20.8 4.2 19.4 4 18 4c-2.3 0-4.4.6-6 1.5zm0 2v10c-1.5-.7-3.3-1-5-1-.7 0-1.4 0-2 .1V6.6c.6-.1 1.3-.1 2-.1 1.7 0 3.5.3 5 1z"/></svg>`;

const num = (v, def) => { const n = parseFloat(v); return Number.isFinite(n) ? n : def; };

// Wrap a raw SVG (single-quoted attrs) into a CSS background `url('data:…')`.
// Inner single quotes are percent-encoded so the value survives BOTH the
// single-quoted url() and the double-quoted style="" attribute it lives in.
const svgBgUrl = (svg) =>
  `url('data:image/svg+xml,${encodeURIComponent(svg).replace(/'/g, '%27')}')`;

// Split the institution name into a large top line + a smaller descriptor line
// (e.g. "RISE ABOVE" / "INTERNATIONAL SCHOOL"), like the reference card. Trailing
// generic words (INTERNATIONAL SCHOOL, JUNIOR COLLEGE, …) drop to the second line.
const NAME_SUFFIX = new Set([
  'SCHOOL', 'COLLEGE', 'ACADEMY', 'INSTITUTE', 'INSTITUTION', 'VIDYALAYA',
  'VIDYALAYAM', 'GURUKUL', 'JUNIOR', 'PUBLIC', 'INTERNATIONAL', 'HIGH',
  'SECONDARY', 'GRAMMAR', 'CONVENT', 'UNIVERSITY',
]);
const splitBrandName = (name) => {
  const n = String(name || 'INSTITUTION NAME').trim();
  const words = n.split(/\s+/);
  if (words.length < 2) return { top: n, bottom: '' };
  if (words.length === 2) return { top: words[0], bottom: words[1] };
  let i = words.length;
  while (i > 1 && NAME_SUFFIX.has(words[i - 1].toUpperCase())) i--;
  if (i >= words.length) i = words.length - 1; // always push at least the last word down
  return { top: words.slice(0, i).join(' '), bottom: words.slice(i).join(' ') };
};

// Clean back-card header: a solid red band with a subtle diagonal bottom and a
// dark-maroon fold in the top-right (matches the front, no pointer tab).
const cardBackBg = (theme = THEME_RED) => {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 204 92' preserveAspectRatio='none'>` +
      `<path d='M0 0 H204 V74 L0 88 Z' fill='${theme.main}'/>` +
      `<path d='M152 0 H204 V56 L152 0 Z' fill='${theme.dark}'/>` +
    `</svg>`;
  return svgBgUrl(svg);
};

// The descriptor line ("HIGH SCHOOL") — ONE shared style so the front and back cards
// render it with identical font, size, weight and tracking, and centred on both.
// (It used to be derived from the front's name size — ~8px, left-aligned — while the
// back hardcoded 9px centred, so the two cards disagreed.)
const SUBTITLE_CSS =
  "color:#fff;font-weight:900;font-size:11px;font-family:Arial,Helvetica,sans-serif;" +
  "text-transform:uppercase;letter-spacing:1.4px;margin-top:3px;text-align:center;" +
  "line-height:1.05;overflow-wrap:anywhere;";

const brandNameBlock = (brand) => {
  const { top, bottom } = splitBrandName(brand.schoolName);
  // Shrink the big line to the longest word so it stays on one line in the header.
  const longest = Math.max(top.length, ...top.split(/\s+/).map(w => w.length));
  // Sized to FIT the ~139px beside the 40px logo even when html2canvas measures the
  // text materially wider than the browser (it does, and it does NOT honour wrapping,
  // so an over-wide header just overflowed and got chopped: "VIDYA BHAR…"). These
  // sizes leave only ~10% headroom — they are AT the limit for the 204px card, so
  // raise them further only by shrinking the logo or the header padding first.
  // (Also deliberately NOT 'Arial Black' — html2canvas substitutes it with something
  // wider still; plain Arial at weight 900 measures consistently.)
  const big   = longest <= 10 ? 17 : longest <= 12 ? 15 : longest <= 14 ? 12.5
              : longest <= 17 ? 10.5 : longest <= 21 ? 9 : 8;
  // `flex:0 1 auto;min-width:0` sizes the block to the NAME, not to the leftover space
  // beside the logo — so the header row can centre logo+name as one group instead of
  // the name floating in the middle of its own 135px box (a visible gap after the logo).
  // `flex-shrink:1;min-width:0` still lets it give way and WRAP rather than
  // `nowrap`-overflow: any renderer that measures the text wider (html2canvas does)
  // used to push it past the 204px card and `overflow:hidden` chopped it
  // ("VIDYA BHAR…"). Worst case now it wraps to a second line — never cut off.
  return `<div style="flex:0 1 auto;min-width:0;line-height:1.05;overflow:hidden;text-align:center;">
    <div style="color:#fff;font-weight:900;font-size:${big}px;font-family:Arial,Helvetica,sans-serif;text-transform:uppercase;letter-spacing:0.3px;line-height:1.05;overflow-wrap:anywhere;">${top}</div>
    ${bottom ? `<div style="${SUBTITLE_CSS}">${bottom}</div>` : ''}
  </div>`;
};

// Full-card art for the front: light-gray diagonal (back), bright-red banner with
// the pointed left "wing" wrapping the photo, and a darker-red rounded panel on
// the right. One SVG data-URI background so it exports cleanly via html2canvas.
const cardFrontBg = (theme = THEME_RED) => {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 204 325' preserveAspectRatio='none'>` +
      // 3) light ash-gray band filling the full wedge on the RIGHT, from the
      //    banner's edge down into the body, above the white
      `<path d='M96 68 L204 108 L204 206 L78 160 Z' fill='#ececec'/>` +
      // 1) main-colour banner filling the entire top, with a clean diagonal edge
      //    line down to the gray on the right and a steep diagonal on the lower-left
      `<path d='M0 0 H204 V108 L96 68 L84 128 L0 58 Z' fill='${theme.main}'/>` +
      // 2) darker fold in the top-right corner (on top of the banner), with a
      //    single soft, lightly-rounded lower edge
      `<path d='M116 0 H204 V106 Q150 68 120 44 Z' fill='${theme.dark}'/>` +
    `</svg>`;
  return svgBgUrl(svg);
};

const infoRow = (icon, value, theme = THEME_RED) =>
  `<div style="display:flex;align-items:flex-start;gap:9px;margin:0 0 2px;">
    <div style="width:19px;height:19px;border-radius:50%;background:${theme.main};display:flex;align-items:center;justify-content:center;flex-shrink:0;">${icon}</div>
    <div style="color:#333;font-size:11px;font-weight:600;line-height:1.25;text-align:left;word-break:break-word;padding-top:2px;">${value}</div>
  </div>`;

function buildEmployeeCardFront(brand, d, sigImg, theme = THEME_RED) {
  const pSrc = d.employee_photo || '';
  const photoFallbackBg = svgBgUrl(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'><rect width='100' height='120' fill='#eee'/><circle cx='50' cy='44' r='24' fill='#ccc'/><ellipse cx='50' cy='110' rx='34' ry='28' fill='#ccc'/></svg>`
  );
  const gpx = num(d.photo_pos_x, 50);
  const gpy = num(d.photo_pos_y, 50);
  const gpz = num(d.photo_zoom, 1);
  const photo = pSrc
    ? `<div style="width:100%;height:100%;background-image:url('${pSrc}');background-repeat:no-repeat;background-position:${gpx}% ${gpy}%;background-size:${gpz > 1 ? (gpz * 100) + '%' : 'cover'};"></div>`
    : `<div style="width:100%;height:100%;background-image:${photoFallbackBg};background-size:cover;background-position:center;background-repeat:no-repeat;"></div>`;

  const logo = brand.logoUrl
    ? `<img src="${brand.logoUrl}" alt="logo" style="width:40px;height:40px;object-fit:contain;border-radius:50%;background:#fff;flex-shrink:0;"/>`
    : '';

  const designationLine = d.designation || '';
  // Mother Teacher handles all subjects — show a dash in the subject slot instead.
  const isMotherTeacher = /mother\s*teacher/i.test(designationLine);
  const subjectLine     = isMotherTeacher ? '-' : (d.subject || '');

  // Icon rows in order: designation, subject, phone — all in the same
  // icon "type" design instead of centred text under the name.
  const rows = [
    [SVG_ROLE,   designationLine],
    [SVG_BOOK,   subjectLine],
    [SVG_PHONE,  d.phone],
  ].filter(([, v]) => v).map(([icon, v]) => infoRow(icon, v, theme)).join('');

  return `<div style="width:${ID_W};height:${ID_H};border-radius:12px;overflow:hidden;box-shadow:0 6px 24px rgba(0,0,0,0.2);font-family:${ID_FONT};background:#fff;position:relative;">
    <div style="position:absolute;inset:0;background-image:${cardFrontBg(theme)};background-size:100% 100%;background-repeat:no-repeat;"></div>

    <div style="position:relative;z-index:1;display:flex;flex-direction:column;height:100%;box-sizing:border-box;">
      <div style="display:flex;align-items:center;justify-content:center;gap:7px;padding:12px 9px 0;flex-shrink:0;">
        ${logo}
        ${brandNameBlock(brand)}
      </div>

      <div style="display:flex;justify-content:center;margin-top:26px;flex-shrink:0;">
        <div style="width:88px;height:88px;border-radius:50%;overflow:hidden;border:4px solid #fff;box-shadow:0 5px 14px rgba(0,0,0,0.22);background:#eee;">${photo}</div>
      </div>

      <div style="text-align:center;color:#1a1a1a;font-weight:900;font-size:15px;margin-top:16px;padding:0 10px;word-break:break-word;line-height:1.1;">${d.employee_name || 'EMPLOYEE NAME'}</div>

      <div style="padding:10px 20px 2px;flex:1;min-height:0;">${rows}</div>

      ${sigImg
        ? `<div style="text-align:right;padding:0 18px 9px;flex-shrink:0;">
             <img src="${sigImg}" style="max-width:96px;max-height:20px;object-fit:contain;display:block;margin:0 0 1px auto;filter:brightness(0);" />
             <div style="color:#555;font-weight:700;font-size:8.5px;letter-spacing:0.4px;">Principal</div>
           </div>`
        : `<div style="text-align:right;padding:0 18px 10px;flex-shrink:0;">
             <div style="width:96px;height:16px;border-bottom:1px solid #c4c4c4;margin:0 0 2px auto;"></div>
             <div style="color:#777;font-weight:700;font-size:8.5px;letter-spacing:0.4px;">Principal</div>
           </div>`}
    </div>
  </div>`;
}

function buildEmployeeCardBack(brand, theme = THEME_RED) {
  const logo = brand.logoUrl
    ? `<img src="${brand.logoUrl}" alt="logo" style="display:block;width:92px;height:92px;object-fit:contain;border-radius:50%;margin:0 auto 14px;"/>`
    : '';
  const addrSplit    = /^(.*?\b\d{6}\b),?\s*(.*)$/.exec((brand.address || '').trim());
  const addressMain  = addrSplit ? addrSplit[1].trim() : (brand.address || '').trim();
  const addressPhone = addrSplit ? addrSplit[2].trim() : '';
  const { top, bottom } = splitBrandName(brand.schoolName);
  // Same reason as the front header: html2canvas measures the text wider and does not
  // honour wrapping, so a fixed 12px overflowed the card and got chopped. Size to fit
  // the ~172px of header width with headroom.
  const topLen  = Math.max(top.length, ...top.split(/\s+/).map((w) => w.length));
  const bigBack = topLen <= 12 ? 16 : topLen <= 15 ? 14 : topLen <= 18 ? 12.5
                : topLen <= 22 ? 10.5 : 9.5;

  const line = (txt) => txt
    ? `<div style="width:100%;margin-top:4px;color:#444;font-size:11px;font-weight:600;line-height:1.55;word-break:break-word;text-align:center;">${txt}</div>`
    : '';

  return `<div style="width:${ID_W};height:${ID_H};border-radius:12px;overflow:hidden;box-shadow:0 6px 24px rgba(0,0,0,0.2);font-family:${ID_FONT};background:#fff;position:relative;display:flex;flex-direction:column;">
    <!-- header (solid red + maroon fold, no pointer) -->
    <div style="position:relative;height:84px;flex-shrink:0;background-image:${cardBackBg(theme)};background-size:100% 100%;background-repeat:no-repeat;">
      <div style="position:relative;text-align:center;padding:19px 16px 0;">
        <div style="color:#fff;font-weight:900;font-size:${bigBack}px;font-family:Arial,Helvetica,sans-serif;text-transform:uppercase;line-height:1.05;overflow-wrap:anywhere;">${top}</div>
        ${bottom ? `<div style="${SUBTITLE_CSS}">${bottom}</div>` : ''}
      </div>
    </div>

    <!-- body -->
    <div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:18px 20px;text-align:center;">
      ${logo}
      ${line(addressMain)}
      ${line(addressPhone)}
      ${brand.phone ? line('Phone: ' + brand.phone) : ''}
    </div>

    <!-- footer bar -->
    <div style="height:12px;background:${theme.main};flex-shrink:0;"></div>
  </div>`;
}

// Full preview markup (front beside back).
function buildEmployeeIdHTML(brand, d, sigImg, theme = THEME_RED) {
  return `<div style="width:100%;display:flex;justify-content:center;align-items:flex-start;gap:24px;padding:28px 0;background:#f0f0f0;min-height:480px;box-sizing:border-box;flex-wrap:wrap;">
    <div id="emp-idcard-front">${buildEmployeeCardFront(brand, d, sigImg, theme)}</div>
    <div id="emp-idcard-back">${buildEmployeeCardBack(brand, theme)}</div>
  </div>`;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const card    = { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: '20px 22px' };
const cardHdr  = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 };
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5,
};
const inputStyle = {
  width: '100%', border: '1.5px solid #d1d5db', borderRadius: 8,
  padding: '9px 12px', fontSize: 13, color: '#1f2937',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
};
const iconWrap = (bg, color) => ({
  background: bg, color, borderRadius: 8, width: 32, height: 32,
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0,
});
const actionBtn = (color) => ({
  background: `${color}12`, color, border: `1.5px solid ${color}40`, borderRadius: 9,
  padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
});

// ─── Component ────────────────────────────────────────────────────────────────

const EmployeeIdCard = ({
  employees = [],
  branches = [],
  savedTemplates = [],
}) => {
  const printAreaRef = useRef(null);

  const [selType,     setSelType]     = useState('');
  const [selEmployee, setSelEmployee] = useState(null);
  const [fieldValues, setFieldValues] = useState({}); // photo + adjust
  const [sigEnabled,  setSigEnabled]  = useState(false);
  const [sigImage,    setSigImage]    = useState('');
  // Principal signature taken from the Student ID card page (persisted there).
  // Used as the default signature; falls back to the ID-card template signature.
  const [studentSig] = useState(() => {
    try { return localStorage.getItem(ID_CARD_PRINCIPAL_SIG_KEY) || ''; } catch { return ''; }
  });
  const [toast,       setToast]       = useState('');
  const [generated,   setGenerated]   = useState(false); // gate for download/print actions
  const [showPreview, setShowPreview] = useState(false); // View ID Card modal

  // ── Card branding from a saved ID-card template (matches the student card) ──
  const brand = useMemo(() => {
    // Collect every student ID-card design; use the first for branding but pull
    // the principal signature from whichever design actually has one, so the
    // employee card defaults to the student ID card's signature even when the
    // first template was saved without it.
    const idCardCfgs = [];
    for (const t of savedTemplates) {
      try {
        const parsed = JSON.parse(t.template);
        if (parsed?.certType === 'idcard') idCardCfgs.push(parsed);
      } catch { /* ignore */ }
    }
    const cfg    = idCardCfgs[0] || null;
    const sigCfg = idCardCfgs.find(c => c?.idCardSignature) || null;
    return {
      schoolName: cfg?.schoolName || Cookies.get('institution_name') || Cookies.get('institutionname') || 'INSTITUTION NAME',
      logoUrl:    cfg?.logoUrl || '',
      address:    cfg?.address || '',
      phone:      cfg?.phone || '',
      idCardSignature: sigCfg?.idCardSignature || '',
    };
  }, [savedTemplates]);

  // Bake the default signature to solid black once (keeping transparency) so it
  // is visible on the white card AND renders identically in the html2canvas
  // export — a CSS `filter` would only affect the on-screen preview, not the PDF.
  const rawDefaultSig = studentSig || brand.idCardSignature || '';
  const [blackSig, setBlackSig] = useState('');
  useEffect(() => {
    if (!rawDefaultSig) { setBlackSig(''); return; }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth || 1;
        c.height = img.naturalHeight || 1;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imgData = ctx.getImageData(0, 0, c.width, c.height);
        const px = imgData.data;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] > 10) { px[i] = 0; px[i + 1] = 0; px[i + 2] = 0; } // ink → black, keep alpha
        }
        ctx.putImageData(imgData, 0, 0);
        if (!cancelled) setBlackSig(c.toDataURL('image/png'));
      } catch { if (!cancelled) setBlackSig(rawDefaultSig); } // tainted canvas — use as-is
    };
    img.onerror = () => { if (!cancelled) setBlackSig(rawDefaultSig); };
    img.src = rawDefaultSig;
    return () => { cancelled = true; };
  }, [rawDefaultSig]);

  // ── Dropdown options ────────────────────────────────────────────────────
  // There is no Branch dropdown: `branches` arrives already narrowed to the
  // header's (NavbarOne) selection. Employees carry branch_name (comma-joined),
  // so the scope is matched by name.
  const scopedBranchNames = useMemo(
    () => new Set(branches.map(b => b.branch_name).filter(Boolean)),
    [branches]
  );

  // "Designation" here is the employee's role (employee_role table). Build the
  // dropdown from the distinct roles of the loaded employees.
  const typeOpts = useMemo(() => {
    const set = new Set();
    employees.forEach(e => { if (e.employee_role) set.add(e.employee_role); });
    return [...set].sort().map(t => ({ value: t, label: t }));
  }, [employees]);

  const employeeOpts = useMemo(() =>
    employees
      .filter(e =>
        (scopedBranchNames.size === 0 ||
          String(e.branch_name || '').split(',').map(s => s.trim()).some(n => scopedBranchNames.has(n))) &&
        (!selType || e.employee_role === selType)
      )
      .sort((a, b) => (a.employee_name || '').localeCompare(b.employee_name || ''))
      .map(e => ({
        value: String(e.id),
        label: e.employee_name,
        employee: e,
      })),
    [employees, scopedBranchNames, selType]
  );

  // ── Auto-fill when an employee is selected ──────────────────────────────
  useEffect(() => {
    setGenerated(false); // changing employee invalidates the generated card
    if (!selEmployee) { setFieldValues({}); return; }
    const photo = normalizePhotoSrc(selEmployee.profile_image);
    const base = {
      photo_pos_x: 50, photo_pos_y: 50, photo_zoom: 1,
      employee_photo: '',
      // Subject(s) auto-filled from the employee record (subject_ids → names,
      // resolved on the backend as `subjects`). Still editable below.
      subject: selEmployee.subjects || '',
    };
    setFieldValues(base);

    // If the employee has a photo, load it (as data URL when possible so the
    // canvas export isn't tainted by a cross-origin image).
    if (photo) {
      if (photo.startsWith('data:')) {
        setFieldValues(prev => ({ ...prev, employee_photo: photo }));
      } else {
        fetch(photo, { mode: 'cors' })
          .then(res => res.blob())
          .then(blob => new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          }))
          .then(dataUrl => setFieldValues(prev => ({ ...prev, employee_photo: dataUrl })))
          .catch(() => setFieldValues(prev => ({ ...prev, employee_photo: photo })));
      }
    }
  }, [selEmployee]);

  // ── Card data ───────────────────────────────────────────────────────────
  const cardData = useMemo(() => {
    if (!selEmployee) return null;
    return {
      employee_name: selEmployee.employee_name,
      employee_id:   selEmployee.id,
      designation:   selEmployee.employee_role || selEmployee.employee_type,
      subject:       fieldValues.subject,
      branch:        (selEmployee.branch_name || '').split(',')[0].trim(),
      email:         selEmployee.emp_email,
      dob:           selEmployee.dob || selEmployee.date_of_birth || '',
      phone:         selEmployee.emp_mobile,
      employee_photo: fieldValues.employee_photo,
      photo_pos_x: fieldValues.photo_pos_x,
      photo_pos_y: fieldValues.photo_pos_y,
      photo_zoom:  fieldValues.photo_zoom,
    };
  }, [selEmployee, fieldValues]);

  // Default signature: the one uploaded on the Student ID card, else the template's,
  // blackened for visibility (falls back to the raw image until baking finishes).
  const defaultSig = blackSig || rawDefaultSig;

  const previewHtml = useMemo(() => {
    if (!cardData) return '';
    return buildEmployeeIdHTML(brand, cardData, sigEnabled ? sigImage : defaultSig, themeFor(cardData.designation));
  }, [brand, cardData, sigEnabled, sigImage, defaultSig]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleFieldChange = (key, val) => setFieldValues(prev => ({ ...prev, [key]: val }));

  // Generate the card — reveals the Print action.
  const handleGenerate = () => {
    setGenerated(true);
    showToast('ID card generated — you can now print or download it.');
  };


  // Print the card HTML directly instead of a html2canvas raster: the rasteriser
  // mis-renders this fixed-px layout (clipped header, stretched background) and the
  // image then had to be force-stretched to mm. The card is authored at 204×325px,
  // which at 96dpi is exactly 54×86mm — so printing the real markup gives true card
  // size, correct proportions and crisp (vector) text.
  const handlePrint = () => {
    const root = printAreaRef.current;
    if (!root) return;
    const front = root.querySelector('#emp-idcard-front');
    const back  = root.querySelector('#emp-idcard-back');
    const cards = [front, back].filter(Boolean).map(el => el.innerHTML).filter(Boolean);
    if (!cards.length) return;

    const body = cards.map(html => `<div class="idpage">${html}</div>`).join('');

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const cleanup = () => { setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1000); };
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><title>Employee ID Card</title>
      <style>
        @page{size:54mm 86mm;margin:0}
        *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box}
        html,body{margin:0;padding:0;background:#fff}
        /* 204×325px @96dpi === 54×86mm — card prints at its true size, no scaling. */
        .idpage{width:204px;height:325px;overflow:hidden;page-break-after:always}
        .idpage:last-child{page-break-after:auto}
        .idpage>div{border-radius:0!important;box-shadow:none!important;margin:0!important}
      </style></head><body>${body}</body></html>`
    );
    doc.close();

    // Wait for logo/photo/signature images, else they print blank.
    const win  = iframe.contentWindow;
    const imgs = Array.from(doc.images || []);
    Promise.all(imgs.map((img) => (img.complete
      ? Promise.resolve()
      : new Promise((res) => { img.onload = res; img.onerror = res; }))))
      .then(() => setTimeout(() => {
        try { win.focus(); win.print(); } catch (e) {}
        cleanup();
      }, 250));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: '#10b981', color: '#fff', borderRadius: 12,
          padding: '12px 20px', fontWeight: 600, fontSize: 13,
          boxShadow: '0 8px 32px #10b98140', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span>✓</span> {toast}
        </div>
      )}

      {/* Selection */}
      <div style={card}>
        <div style={cardHdr}>
          <div style={iconWrap('#E1232A15', ACCENT)}>
            <i className="fal fa-id-badge" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>Employee ID Card</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Pick an employee to generate their ID card. Designation is an optional filter.
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {/* No Branch filter — the header's branch selection scopes these employees. */}
          <div>
            <label style={labelStyle}>Designation</label>
            <select
              value={selType}
              onChange={e => { setSelType(e.target.value); setSelEmployee(null); }}
              style={inputStyle}
            >
              <option value="">-- All Designations --</option>
              {typeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Employee <span style={{ color: '#ef4444' }}>*</span></label>
            <Select
              classNamePrefix="emp-id-select"
              placeholder="Search or select employee…"
              isClearable
              noOptionsMessage={() => 'No employees found'}
              options={employeeOpts}
              value={selEmployee ? employeeOpts.find(o => o.value === String(selEmployee.id)) || {
                value: String(selEmployee.id),
                label: selEmployee.employee_name,
              } : null}
              onChange={opt => setSelEmployee(opt ? opt.employee : null)}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                control: (base, state) => ({
                  ...base, minHeight: 38, borderRadius: 8, borderWidth: 1.5,
                  borderColor: state.isFocused ? ACCENT : '#d1d5db', boxShadow: 'none', fontSize: 13,
                  '&:hover': { borderColor: ACCENT },
                }),
                valueContainer: base => ({ ...base, padding: '2px 10px' }),
                placeholder: base => ({ ...base, color: '#9ca3af' }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menu: base => ({ ...base, fontSize: 13 }),
                option: (base, state) => ({
                  ...base, fontSize: 13,
                  backgroundColor: state.isSelected ? ACCENT : state.isFocused ? '#fdecec' : '#fff',
                  color: state.isSelected ? '#fff' : '#1f2937',
                }),
              }}
            />
          </div>
        </div>

        {/* Selected employee chip */}
        {selEmployee && (
          <div style={{ marginTop: 16, background: '#fdecec', border: '1px solid #f3b5b8', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: ACCENT }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fal fa-check-circle" />
              <strong>{selEmployee.employee_name}</strong>
              {selEmployee.employee_role && <span> · {selEmployee.employee_role}</span>}
              {selEmployee.department_name && <span> · {selEmployee.department_name}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Form + Preview */}
      {selEmployee && (
        <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20, alignItems: 'start' }}>

          {/* Left: subject + photo + signature + adjust */}
          <div style={card}>
            {/* Subject */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Subject</label>
              <input
                type="text"
                value={fieldValues.subject || ''}
                placeholder="e.g. Mathematics"
                onChange={e => handleFieldChange('subject', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Photo */}
            <label style={labelStyle}>Employee Photo</label>
            <label style={{
              display: 'block', border: `2px dashed ${fieldValues.employee_photo ? '#86efac' : '#d1d5db'}`,
              borderRadius: 10, padding: '14px 20px', cursor: 'pointer', textAlign: 'center',
              background: fieldValues.employee_photo ? '#f0fdf4' : '#fafafa', maxWidth: 380,
            }}>
              {fieldValues.employee_photo ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img src={fieldValues.employee_photo} alt="employee" style={{ borderRadius: '50%', width: 72, height: 72, objectFit: 'cover', border: '2px solid #86efac' }} />
                  <button type="button" onClick={e => { e.preventDefault(); handleFieldChange('employee_photo', ''); }}
                    style={{ background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
                    × Remove
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 26, marginBottom: 4 }}>📷</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Click to upload Employee Photo</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>PNG, JPG — max 2 MB · auto-loaded from record if available</div>
                </>
              )}
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onloadend = () => handleFieldChange('employee_photo', reader.result);
                  reader.readAsDataURL(file);
                }}
              />
            </label>

            {/* Photo adjust */}
            {fieldValues.employee_photo && (
              <div style={{ marginTop: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>
                    <i className="fal fa-crop-alt" style={{ color: ACCENT, marginRight: 6 }} />Adjust Photo
                  </span>
                  <button type="button"
                    onClick={() => setFieldValues(prev => ({ ...prev, photo_pos_x: 50, photo_pos_y: 50, photo_zoom: 1 }))}
                    style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, color: '#6b7280', padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
                    Reset
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
                  {[
                    { key: 'photo_pos_x', label: 'Horizontal', min: 0, max: 100, step: 1, def: 50, suffix: '%' },
                    { key: 'photo_pos_y', label: 'Vertical',   min: 0, max: 100, step: 1, def: 50, suffix: '%' },
                    { key: 'photo_zoom',  label: 'Zoom',        min: 1, max: 3,   step: 0.05, def: 1, suffix: '×' },
                  ].map(({ key, label, min, max, step, def, suffix }) => {
                    const val = fieldValues[key] != null ? Number(fieldValues[key]) : def;
                    return (
                      <div key={key}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
                          <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>{val}{suffix}</span>
                        </div>
                        <input type="range" min={min} max={max} step={step} value={val}
                          onChange={e => handleFieldChange(key, Number(e.target.value))}
                          style={{ width: '100%', accentColor: ACCENT, cursor: 'pointer' }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Signature */}
            <div style={{ marginTop: 20, borderTop: '1.5px dashed #e2e8f0', paddingTop: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={sigEnabled}
                  onChange={e => { setSigEnabled(e.target.checked); if (!e.target.checked) setSigImage(''); }}
                  style={{ width: 16, height: 16, accentColor: ACCENT, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>Use a custom principal signature</span>
              </label>
              {/* By default the card shows the principal signature configured on the
                  student ID-card design; ticking the box overrides it with an upload. */}
              {!sigEnabled && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, maxWidth: 360 }}>
                  {brand.idCardSignature ? (
                    <>
                      <img src={brand.idCardSignature} alt="principal signature"
                        style={{ maxWidth: 120, maxHeight: 40, objectFit: 'contain', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 4 }} />
                      <span style={{ fontSize: 11.5, color: '#6b7280' }}>Default principal signature from the ID card design.</span>
                    </>
                  ) : (
                    <span style={{ fontSize: 11.5, color: '#9ca3af' }}>No principal signature set on the ID card design — tick above to upload one.</span>
                  )}
                </div>
              )}
              {sigEnabled && (
                <label style={{
                  display: 'block', border: `2px dashed ${sigImage ? '#86efac' : '#d1d5db'}`, borderRadius: 10,
                  padding: '14px 20px', cursor: 'pointer', textAlign: 'center', marginTop: 12,
                  background: sigImage ? '#f0fdf4' : '#fafafa', maxWidth: 360,
                }}>
                  {sigImage ? (
                    <div>
                      <img src={sigImage} alt="sig" style={{ maxWidth: 200, maxHeight: 70, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                      <button type="button" onClick={e => { e.preventDefault(); setSigImage(''); }}
                        style={{ marginTop: 8, background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>× Remove</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 26, marginBottom: 4 }}>✍</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>Click to upload signature image</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>PNG, JPG, SVG — transparent background recommended</div>
                    </>
                  )}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0]; if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = () => setSigImage(reader.result);
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>

            {/* Actions */}
            <div style={{ marginTop: 22, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                onClick={() => setShowPreview(true)}
                style={{
                  background: '#fff', color: ACCENT, border: `2px solid ${ACCENT}`,
                  borderRadius: 10, padding: '11px 24px', fontWeight: 700, fontSize: 14,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <i className="fal fa-eye" /> View ID Card
              </button>
              <button
                onClick={handleGenerate}
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}cc)`,
                  color: '#fff', border: 'none', borderRadius: 10,
                  padding: '11px 28px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8, boxShadow: `0 4px 14px ${ACCENT}44`,
                }}
              >
                <i className="fal fa-id-card" /> Generate
              </button>
              {generated && (
                <>
                  <button onClick={handlePrint} style={actionBtn('#0891b2')}>🖨 Print</button>
                </>
              )}
            </div>
          </div>

          {/* Right: live preview */}
          <div style={{ ...card, padding: '16px', position: 'sticky', top: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, background: ACCENT, borderRadius: '50%', display: 'inline-block' }} />
              <span style={{ fontWeight: 700, fontSize: 12, color: '#374151', textTransform: 'uppercase', letterSpacing: 1 }}>
                Live Preview
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: ACCENT, fontWeight: 600, background: '#fdecec', borderRadius: 5, padding: '2px 8px' }}>
                Front · Back
              </span>
            </div>
            <div style={{
              background: '#e8edf5', borderRadius: 10, padding: '14px',
              border: '1px solid #d0d8e8', display: 'flex', justifyContent: 'center', overflow: 'hidden',
            }}>
              <div
                style={{ zoom: 0.62, background: '#f0f0f0', flexShrink: 0 }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}

      {/* View ID Card modal */}
      {showPreview && cardData && (() => {
        const modalSig = sigEnabled ? sigImage : defaultSig;
        const sideBox = {
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        };
        const sideLabel = {
          fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#6b7280',
          textTransform: 'uppercase', background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 20, padding: '4px 16px',
        };
        return (
          <div
            onClick={() => setShowPreview(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 10000,
              background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(3px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 18, width: 'auto', maxWidth: '94vw', maxHeight: '94vh',
                overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {/* Header — olive gradient bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '16px 22px', background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}cc)`,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: 'rgba(255,255,255,0.18)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>
                  <i className="fal fa-id-badge" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>
                    Employee ID Card
                  </div>
                  <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selEmployee?.employee_name}
                    {selEmployee?.employee_role ? ` · ${selEmployee.employee_role}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  title="Close"
                  style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
                    cursor: 'pointer', fontSize: 18, lineHeight: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >×</button>
              </div>

              {/* Body — framed front & back, individually labelled */}
              <div style={{
                padding: '28px 32px', overflow: 'auto',
                background: 'linear-gradient(180deg, #f7f8f3 0%, #eef0e6 100%)',
                display: 'flex', gap: 36, justifyContent: 'center', flexWrap: 'wrap',
              }}>
                <div style={sideBox}>
                  <div style={{ filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.18))' }}
                    dangerouslySetInnerHTML={{ __html: buildEmployeeCardFront(brand, cardData, modalSig, themeFor(cardData.designation)) }} />
                  <span style={sideLabel}>Front</span>
                </div>
                <div style={sideBox}>
                  <div style={{ filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.18))' }}
                    dangerouslySetInnerHTML={{ __html: buildEmployeeCardBack(brand, themeFor(cardData.designation)) }} />
                  <span style={sideLabel}>Back</span>
                </div>
              </div>

              {/* Footer — actions */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '14px 22px', borderTop: '1px solid #e5e7eb', background: '#fff',
              }}>
                <button onClick={handlePrint} style={actionBtn('#0891b2')}>🖨 Print</button>
                <button
                  onClick={() => setShowPreview(false)}
                  style={{
                    marginLeft: 'auto', background: '#fff', color: '#374151',
                    border: '1.5px solid #d1d5db', borderRadius: 9,
                    padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Hidden element for Print / PDF / Image capture */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={printAreaRef} style={{ background: '#fff' }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>
    </div>
  );
};

export default EmployeeIdCard;
